import React, { useState, useEffect } from 'react';
import { FileText, Edit, Trash2, Eye, Calendar, User, Clock, Search, Plus, Tag, ArrowLeft } from 'lucide-react';
import { db } from './firebase';
import { collection, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';

const ViewBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [editingBlog, setEditingBlog] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  const categories = [
    'All',
    'Technology',
    'Insurance',
    'Wellness',
    'Healthcare',
    'Medical',
    'Tips',
    'News',
    'Research'
  ];

  useEffect(() => {
    loadBlogs();
  }, []);

  const loadBlogs = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'blogs'));
      const blogsData = [];
      querySnapshot.forEach((doc) => {
        blogsData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      // Sort by creation date (newest first)
      blogsData.sort((a, b) => {
        if (a.timestamp && b.timestamp) {
          return b.timestamp - a.timestamp;
        }
        return new Date(b.date) - new Date(a.date);
      });
      setBlogs(blogsData);
    } catch (error) {
      console.error('Error loading blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (blogId, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        await deleteDoc(doc(db, 'blogs', blogId));
        await loadBlogs();
        alert('Blog deleted successfully!');
      } catch (error) {
        console.error('Error deleting blog:', error);
        alert('Error deleting blog. Please try again.');
      }
    }
  };

  const startEdit = (blog) => {
    setEditingBlog(blog.id);
    setEditFormData({
      title: blog.title,
      excerpt: blog.excerpt,
      author: blog.author,
      readTime: blog.readTime,
      category: blog.category,
      content: blog.content
    });
  };

  const cancelEdit = () => {
    setEditingBlog(null);
    setEditFormData({});
  };

  const handleUpdate = async (blogId) => {
    try {
      await updateDoc(doc(db, 'blogs', blogId), {
        ...editFormData,
        updatedAt: new Date().toISOString()
      });
      await loadBlogs();
      setEditingBlog(null);
      setEditFormData({});
      alert('Blog updated successfully!');
    } catch (error) {
      console.error('Error updating blog:', error);
      alert('Error updating blog. Please try again.');
    }
  };

  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === '' || selectedCategory === 'All' || blog.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-emerald-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => window.history.back()}
            className="flex items-center text-emerald-600 hover:text-emerald-700 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-green-600 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center">
                  <FileText className="w-8 h-8 mr-3" />
                  Blog Management
                </h1>
                <p className="text-emerald-100 mt-2">View, edit, and manage all blog posts</p>
              </div>
              <div className="bg-white/20 rounded-lg px-4 py-2">
                <span className="text-white font-semibold">{blogs.length} Posts</span>
              </div>
            </div>
          </div>

          <div className="p-8">
            {/* Filters and Search */}
            <div className="mb-8 flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search blogs by title, author, or content..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>
              <div className="md:w-64">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  {categories.map(category => (
                    <option key={category} value={category === 'All' ? '' : category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Results Summary */}
            <div className="mb-6">
              <p className="text-gray-600">
                Showing {filteredBlogs.length} of {blogs.length} blog posts
                {searchTerm && ` matching "${searchTerm}"`}
                {selectedCategory && selectedCategory !== 'All' && ` in ${selectedCategory}`}
              </p>
            </div>

            {/* Blogs List */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
              </div>
            ) : filteredBlogs.length > 0 ? (
              <div className="space-y-6">
                {filteredBlogs.map((blog) => (
                  <div key={blog.id} className="bg-gray-50 rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300">
                    {editingBlog === blog.id ? (
                      /* Edit Form */
                      <div className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
                            <input
                              type="text"
                              value={editFormData.title}
                              onChange={(e) => setEditFormData(prev => ({ ...prev, title: e.target.value }))}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                            <select
                              value={editFormData.category}
                              onChange={(e) => setEditFormData(prev => ({ ...prev, category: e.target.value }))}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            >
                              {categories.slice(1).map(category => (
                                <option key={category} value={category}>{category}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Author</label>
                            <input
                              type="text"
                              value={editFormData.author}
                              onChange={(e) => setEditFormData(prev => ({ ...prev, author: e.target.value }))}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Read Time</label>
                            <input
                              type="text"
                              value={editFormData.readTime}
                              onChange={(e) => setEditFormData(prev => ({ ...prev, readTime: e.target.value }))}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Excerpt</label>
                          <textarea
                            value={editFormData.excerpt}
                            onChange={(e) => setEditFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                            rows="3"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Content</label>
                          <textarea
                            value={editFormData.content}
                            onChange={(e) => setEditFormData(prev => ({ ...prev, content: e.target.value }))}
                            rows="10"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                          />
                        </div>

                        <div className="flex space-x-3">
                          <button
                            onClick={() => handleUpdate(blog.id)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center"
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Update Blog
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Display View */
                      <div>
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium mr-3 ${
                                blog.category === 'Technology' ? 'bg-blue-100 text-blue-700' :
                                blog.category === 'Healthcare' ? 'bg-green-100 text-green-700' :
                                blog.category === 'Wellness' ? 'bg-purple-100 text-purple-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {blog.category}
                              </span>
                              <span className="text-sm text-gray-500">{blog.date}</span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2 hover:text-emerald-600 transition-colors">
                              {blog.title}
                            </h3>
                            <p className="text-gray-600 mb-3 line-clamp-2">{blog.excerpt}</p>
                            <div className="flex items-center text-sm text-gray-500 space-x-4">
                              <div className="flex items-center">
                                <User className="w-4 h-4 mr-1" />
                                {blog.author}
                              </div>
                              <div className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                {blog.readTime}
                              </div>
                              <div className="flex items-center">
                                <Calendar className="w-4 h-4 mr-1" />
                                {blog.date}
                              </div>
                            </div>
                          </div>
                          {blog.image && (
                            <div className="ml-6 flex-shrink-0">
                              <img
                                src={blog.image}
                                alt={blog.title}
                                className="w-24 h-16 object-cover rounded-lg"
                              />
                            </div>
                          )}
                        </div>

                        <div className="flex space-x-3">
                          <button
                            onClick={() => startEdit(blog)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(blog.id, blog.title)}
                            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </button>
                          <button
                            onClick={() => window.open(`/blog/${blog.id}`, '_blank')}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-gray-50 rounded-2xl border border-gray-200">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-semibold text-gray-500 mb-2">
                  {searchTerm || selectedCategory ? 'No blogs found' : 'No blogs published yet'}
                </h3>
                <p className="text-gray-400 mb-4">
                  {searchTerm || selectedCategory 
                    ? 'Try adjusting your search criteria' 
                    : 'Start by creating your first blog post'
                  }
                </p>
                <button className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center mx-auto">
                  <Plus className="w-5 h-5 mr-2" />
                  Add New Blog
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewBlogs;
